import { Button } from '@/components/ui/button';
import { useOrderFormContext } from '../OrderFormContext';
import { cn } from '@/lib/utils';
import { useMemo, ReactNode } from 'react';
import SvgIcon from '@/components/Icon/SvgIcon';
import i18n from '@/utils/i18n';
import { useFormState } from 'react-hook-form';
import { BottomBar } from '@/components/BottomBar/BottomBar';

type BottomBarProps = {
  className?: string;
  icon?: ReactNode;
  text: ReactNode;
  children: ReactNode;
};

export function EventOrderBottomBar({
  className,
  icon,
  text,
  children,
}: BottomBarProps) {
  return (
    <BottomBar>
      <div className={cn('tw:p-6 tw:text-black', className)}>
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:flex-wrap">
          <div className="tw:flex tw:items-center tw:gap-2">
            {icon}
            <span className="tw:text-sm tw:font-medium">{text}</span>
          </div>
          <div className="tw:flex-1 tw:flex tw:flex-wrap tw:justify-end tw:gap-2">
            {children}
          </div>
        </div>
      </div>
    </BottomBar>
  );
}

type Status =
  | 'not-accepted'
  | 'accepted'
  | 'invalid'
  | 'valid'
  | 'loading'
  | 'submit-error'
  | 'submitted-locked'
  | 'submitted-unlocked'
  | 'expired';

type StatusPreset = {
  text: React.ReactNode;
  icon?: React.ReactNode;
  button: React.ReactNode;
};

const STATUS_BG_COLORS: Record<Status, string> = {
  'not-accepted': 'tw:bg-primary-ultralight',
  accepted: 'tw:bg-primary-ultralight',
  invalid: 'tw:bg-red-50',
  valid: 'tw:bg-green-50',
  loading: 'tw:bg-gray-50',
  'submit-error': 'tw:bg-red-50',
  'submitted-locked': 'tw:bg-success-ultralight',
  'submitted-unlocked': 'tw:bg-green-50',
  expired: 'tw:bg-primary-ultralight',
};

EventOrderBottomBar.Connected = function BottomBarConnected() {
  const {
    formLoadingStatus,
    errorMessage,
    formControl,
    fetchedFormInfo,
    termsAndConditionsAccepted,
    acceptTermsAndConditions,
    submitForm,
    order,
    resetForm,
    alreadySubmittedLock,
    unlockAlreadySubmittedLock,
  } = useOrderFormContext();

  const hasItems = useMemo(() => {
    return Object.values(order).some(
      (loc) =>
        Object.values(loc.containers).some((c) => c.quantity > 0) ||
        Object.values(loc.itemTypes ?? {}).some((line) => line.quantity > 0),
    );
  }, [order]);

  const canTrySubmitting = formLoadingStatus === 'ready' && hasItems;

  const { isValid: isFormValid, submitCount } = useFormState({
    control: formControl,
  });

  const deadline = useMemo(
    () =>
      fetchedFormInfo?.event?.merchant_order_form_deadline
        ? new Date(fetchedFormInfo.event.merchant_order_form_deadline)
        : null,
    [fetchedFormInfo?.event?.merchant_order_form_deadline],
  );

  const alreadySubmitted = !!fetchedFormInfo?.ops_order?.latest_revision;

  const eventManager = useMemo(
    () => fetchedFormInfo?.event?.event_manager,
    [fetchedFormInfo?.event?.event_manager],
  );

  const status: Status = useMemo(() => {
    // Check if form has expired (can't submit or edit anymore)
    if (fetchedFormInfo && !fetchedFormInfo.can_submit_or_edit) {
      return 'expired';
    }
    if (formLoadingStatus === 'submit-error') return 'submit-error';
    if (formLoadingStatus === 'submitting') return 'loading';
    if (alreadySubmitted && submitCount === 0) {
      return alreadySubmittedLock ? 'submitted-locked' : 'submitted-unlocked';
    }
    if (!termsAndConditionsAccepted) return 'not-accepted';
    if (termsAndConditionsAccepted && submitCount === 0) return 'accepted';
    if (!isFormValid) return 'invalid';
    return 'valid';
  }, [
    fetchedFormInfo,
    formLoadingStatus,
    alreadySubmitted,
    alreadySubmittedLock,
    termsAndConditionsAccepted,
    submitCount,
    isFormValid,
  ]);

  const content: Record<Status, StatusPreset> = {
    'not-accepted': {
      text: i18n.t('EventOrder:pleaseAcceptConditions'),
      icon: <SvgIcon name="light-bulb-idea" size={20} />,
      button: (
        <Button
          onClick={acceptTermsAndConditions}
          className="tw:dark:bg-neutral-900 tw:dark:text-white"
        >
          {i18n.t('EventOrder:acceptTermsAndConditions')}
        </Button>
      ),
    },
    'submit-error': {
      text: errorMessage || i18n.t('Common:error'),
      icon: <SvgIcon name="warning-triangle" size={20} />,
      button: (
        <Button
          onClick={submitForm}
          className="tw:dark:bg-neutral-900 tw:dark:text-white"
        >
          {i18n.t('EventOrder:submitOrder')}
        </Button>
      ),
    },
    invalid: {
      text: i18n.t('EventOrder:formContainsErrors'),
      icon: <SvgIcon name="warning-triangle" size={20} />,
      button: (
        <Button
          disabled={!canTrySubmitting}
          onClick={submitForm}
          className="tw:dark:bg-neutral-900 tw:dark:text-white"
        >
          {i18n.t('EventOrder:fixErrorsToContinue')}
        </Button>
      ),
    },
    valid: {
      text: i18n.t('EventOrder:orderReadyToSubmit'),
      icon: <SvgIcon name="calendar" size={20} />,
      button: (
        <Button
          disabled={!canTrySubmitting}
          onClick={submitForm}
          className="tw:dark:bg-neutral-900 tw:dark:text-white"
        >
          {i18n.t('EventOrder:submitOrder')}
        </Button>
      ),
    },
    loading: {
      text: i18n.t('EventOrder:orderBeingSubmitted'),
      icon: (
        <SvgIcon
          name="arrows-circle-refresh"
          size={20}
          className="tw:animate-spin"
        />
      ),
      button: <Button disabled>{i18n.t('Common:pleaseWait')}</Button>,
    },
    accepted: {
      text: i18n.t('EventOrder:formWillExpire', {
        date: deadline?.toLocaleString(undefined, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
      }),
      icon: <SvgIcon name="warning-triangle" size={20} />,
      button: (
        <Button
          disabled={!canTrySubmitting || (submitCount > 0 && !isFormValid)}
          onClick={submitForm}
          className="tw:dark:bg-neutral-900 tw:dark:text-white"
        >
          {i18n.t('EventOrder:submitOrder')}
        </Button>
      ),
    },
    'submitted-locked': {
      text: i18n.t('EventOrder:orderSubmittedCanEdit', {
        date: deadline?.toLocaleDateString(undefined, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
      }),
      icon: <SvgIcon name="checkmark" size={20} />,
      button: (
        <Button
          onClick={unlockAlreadySubmittedLock}
          className="tw:dark:bg-neutral-900 tw:dark:text-white"
        >
          <SvgIcon name="pencil-edit-frame" size={20} />
          {i18n.t('EventOrder:editOrder')}
        </Button>
      ),
    },
    'submitted-unlocked': {
      text: i18n.t('EventOrder:canNowEditOrder'),
      icon: <SvgIcon name="pencil-edit-frame" size={20} />,
      button: (
        <>
          <Button
            variant="destructive"
            onClick={resetForm}
            className="tw:text-destructive-foreground"
          >
            <SvgIcon name="pencil-edit-frame" size={20} />
            {i18n.t('Common:discardChanges')}
          </Button>
          <Button
            onClick={submitForm}
            // className="tw:dark:bg-neutral-900 tw:dark:text-white"
          >
            <SvgIcon name="arrows-circle-refresh" size={20} />
            {i18n.t('EventOrder:updateOrder')}
          </Button>
        </>
      ),
    },
    expired: {
      text: (() => {
        if (!eventManager) {
          return i18n.t('EventOrder:cannotEditContactManager');
        }

        const phone = eventManager.phone || eventManager.mobile;
        if (!phone) {
          return i18n.t('EventOrder:cannotEditExpiredNoPhone', {
            name: eventManager.full_name,
          });
        }

        // Split translation at {{phone}} placeholder for custom rendering
        // This project needs react-i18next
        const text = i18n.t('EventOrder:cannotEditExpiredWithPhone', {
          name: eventManager.full_name,
          phone: '{{PHONE_LINK}}',
        });

        const [before, after] = text.split('{{PHONE_LINK}}');

        return (
          <>
            {before}
            <a
              href={`tel:${phone}`}
              className="tw:underline tw:font-medium tw:hover:text-primary"
            >
              {phone}
            </a>
            {after}
          </>
        );
      })(),
      icon: <SvgIcon name="light-bulb-idea" size={20} />,
      button: (
        <Button disabled>
          <SvgIcon name="pencil-edit-frame" size={20} />
          {i18n.t('EventOrder:editOrder')}
        </Button>
      ),
    },
  };

  // Don't show bottom bar while loading, if link is invalid, or on fetch error
  if (
    formLoadingStatus === 'initializing' ||
    formLoadingStatus === 'loading' ||
    formLoadingStatus === 'invalid-link' ||
    formLoadingStatus === 'fetch-error'
  ) {
    return null;
  }

  return (
    <EventOrderBottomBar
      className={STATUS_BG_COLORS[status]}
      icon={content[status]?.icon}
      text={content[status].text}
    >
      {content[status]?.button}
    </EventOrderBottomBar>
  );
};
